/*
 * Copyright © 2024. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import { dateFieldDefault, instituteReferenceDefault, textFieldDefault } from "../../../validationRules";
import * as stagesSettlement from "../../../Stages/StagesSettlement";
import { formValue } from "../../../formServiceFunctions";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import { FormDefinition } from "../../../../types/ProductService/ProductService";
import { FormElementPropsWithoutProduct } from "../../../../components/Case/CaseForm/FormElement";
import { Settlement } from "../../../../types/Products/EmploymentLaw/Settlement";

const lateFinancialInfo: FormElementPropsWithoutProduct<Settlement>[] = [
  {
    type: "ValidatorDateField",
    path: "settlement.settlementRevocationDate",
    validators: dateFieldDefault,
    isDisabled: ({ product, values }) => !!values[product.productClassName].finalJudgement,
  },
  {
    type: "ValidatorNumberField",
    path: "settlement.valueInDispute",
    fieldType: "money",
    numberType: "float",
  },
  {
    type: "ValidatorNumberField",
    path: "settlement.nWnFAmount",
    isHidden: ({ product }) => product.paymentType !== "nwnf",
    fieldType: "money",
    numberType: "float",
  },
];

const settlementCourtFields: FormElementPropsWithoutProduct<Settlement>[] = [
  {
    type: "ValidatorDateField",
    path: "settlement.settlementReachedDate",
    validators: dateFieldDefault,
    isDisabled: ({ product, values }) => !!values[product.productClassName].finalJudgement,
  },
];

const preChecked: FormElementPropsWithoutProduct<Settlement>[] = [
  {
    type: "ValidatorDateField",
    path: "settlement.dismissalProtectionSuitDeadline",
    validators: dateFieldDefault,
  },
  {
    type: "ValidatorTextField",
    path: "settlement.powerOfAttorney",
    isDisabled: () => true,
  },
  {
    type: "ValidatorSelect",
    path: "settlement.paymentType",
    options: () => [{ value: "__null__" }, { value: "insurance" }, { value: "nwnf" }, { value: "private" }],
  },
  {
    type: "ValidatorNumberField",
    path: "settlement.nWnFAmount",
    isHidden: ({ values }) => formValue(values, "settlement.paymentType") !== "nwnf",
    fieldType: "money",
    numberType: "float",
  },
  {
    type: "ValidatorSelect",
    path: "settlement.contractTerminated",
    options: () => trueFalseDefaultOptions("settlement.contractTerminated"),
  },
  {
    type: "ValidatorSelect",
    path: "settlement.numberOfEmployees",
    options: () => [{ value: "__null__" }, { value: 10 }, { value: 50 }, { value: 500 }, { value: 1000 }],
  },
  {
    type: "ValidatorDateField",
    path: "settlement.employedSince",
    validators: [...dateFieldDefault],
  },
  {
    type: "ValidatorNumberField",
    path: "settlement.grossSalary",
    fieldType: "money",
    numberType: "float",
  },
  {
    type: "ValidatorTextField",
    path: "settlement.jobDescription",
    validators: [...textFieldDefault],
  },
  {
    type: "ValidatorSelect",
    path: "settlement.worksCouncil",
    options: () => trueFalseDefaultOptions("settlement.worksCouncil"),
  },
  {
    type: "ValidatorSelect",
    path: "settlement.terminationObstacles",
    options: ({ values, product }) => {
      let obstacles = [];
      obstacles.push({ value: "parent" });
      product.petitioner.gender === "female" && obstacles.push({ value: "pregnant" });
      obstacles.push({ value: "disabled" });
      formValue(values, "settlement.worksCouncil") === true && obstacles.push({ value: "council" });
      obstacles.push({ value: "privacyOfficer" });
      obstacles.push({ value: "apprentice" });
      return obstacles;
    },
    additionalProps: {
      multiple: true,
    },
  },
  {
    type: "ValidatorDateField",
    path: "settlement.contractTerminationReceivedDate",
    validators: [...dateFieldDefault],
  },
  {
    type: "ValidatorSelect",
    path: "settlement.terminationNoticeType",
    options: () => [{ value: "__null__" }, { value: "withoutNotice" }, { value: "notice" }, { value: "unsure" }],
  },
  {
    type: "ValidatorSelect",
    path: "settlement.preferredOutcome",
    options: () => [{ value: "__null__" }, { value: "settlement" }, { value: "employment" }],
  },
];

export const stageFormDefinition: ReturnType<FormDefinition<Settlement>> = {
  [stagesSettlement.STAGE_INCOMPLETE]: preChecked,
  [stagesSettlement.STAGE_READY]: preChecked,
  [stagesSettlement.STAGE_CHECKED]: [
    {
      type: "ValidatorDateField",
      path: "settlement.dismissalProtectionSuitDeadline",
      isDisabled: () => true,
    },
    {
      type: "ValidatorDateField",
      path: "settlement.insuranceCoverageDate",
      isHidden: ({ product }) => product.paymentType !== "insurance",
      validators: dateFieldDefault,
    },
  ],
  [stagesSettlement.STAGE_SUBMITTED]: [
    {
      type: "ValidatorDateField",
      path: "settlement.dismissalProtectionSuitDeadline",
      isDisabled: () => true,
    },
    {
      type: "ValidatorDateField",
      path: "settlement.dismissalProtectionSuitSubmittedDate",
      isDisabled: () => true,
    },
    {
      type: "ValidatorTextField",
      path: "settlement.courtReference",
      validators: instituteReferenceDefault,
    },
    {
      type: "ValidatorDateField",
      path: "settlement.insuranceCoverageDate",
      isHidden: ({ product }) => product.paymentType !== "insurance",
      validators: dateFieldDefault,
    },
    ...settlementCourtFields,
  ],
  [stagesSettlement.STAGE_PAID]: [
    {
      type: "ValidatorTextField",
      path: "settlement.courtReference",
      validators: instituteReferenceDefault,
    },
    {
      type: "ValidatorDateField",
      path: "settlement.insuranceCoverageDate",
      isHidden: ({ product }) => product.paymentType !== "insurance",
      validators: dateFieldDefault,
    },
    ...settlementCourtFields,
  ],
  [stagesSettlement.STAGE_DATE_SET_SETTLEMENT]: [
    {
      type: "ValidatorTextField",
      path: "settlement.courtReference",
      validators: instituteReferenceDefault,
    },
    ...settlementCourtFields,
    ...lateFinancialInfo,
  ],
  [stagesSettlement.STAGE_DATE_SET_TRIAL]: [
    {
      type: "ValidatorTextField",
      path: "settlement.courtReference",
      validators: instituteReferenceDefault,
    },
    ...settlementCourtFields,
    {
      type: "ValidatorDateField",
      path: "settlement.finalJudgement",
      validators: dateFieldDefault,
      isHidden: ({ values }) => !!formValue(values, "settlement.settlementReachedDate"),
    },
    ...lateFinancialInfo,
  ],
  [stagesSettlement.STAGE_DECISION_MADE]: [
    {
      type: "ValidatorTextField",
      path: "settlement.courtReference",
      validators: instituteReferenceDefault,
    },
    ...settlementCourtFields,
    {
      type: "ValidatorDateField",
      path: "settlement.finalJudgement",
      validators: dateFieldDefault,
      isHidden: ({ values }) => !!formValue(values, "settlement.settlementReachedDate"),
    },
    ...lateFinancialInfo,
  ],
  [stagesSettlement.STAGE_COMPLETED]: [
    {
      type: "ValidatorDateField",
      path: "settlement.settlementReachedDate",
      validators: dateFieldDefault,
      isDisabled: () => true,
      isHidden: ({ product }) => !product.settlementReachedDate,
    },
    {
      type: "ValidatorDateField",
      path: "settlement.finalJudgement",
      validators: dateFieldDefault,
      isDisabled: () => true,
      isHidden: ({ product }) => !product.finalJudgement,
    },
    {
      type: "ValidatorTextField",
      path: "settlement.courtReference",
      validators: instituteReferenceDefault,
    },
  ],
};
